// Pipeline's table, for broker LOIs: one row per LOI, sortable on every
// column. An em dash means the figure is not held, not that it is zero.

import { useMemo, useState } from "react";
import { STAGE_COLORS } from "../../constants/dealStages";
import { followUpDays, fmtMoneyShort, fmtShortDate } from "../../services/outreachFlow";

const sf0 = (v) => (Number(v) ? Number(v).toLocaleString() : "—");
const named = (d) => (d.sourceNotes?.match(/^Name:\s*(.+)$/m)?.[1]) || d.addresses?.[0] || "Untitled";

const COLUMNS = [
  { k: "name", label: "Deal", w: "min-w-[14rem]", align: "left", get: (d) => d._name,
    render: (d) => (
      <div className="min-w-0">
        <div className="text-[#f4f4f4] truncate">{d._name}</div>
        <div className="text-[11px] text-[#8a8a8a] truncate">
          {d.addresses?.[0] || "—"}{d.addresses?.length > 1 ? ` +${d.addresses.length - 1} more` : ""}
        </div>
      </div>
    ) },
  { k: "stage", label: "Stage", align: "left", get: (d) => d.stage || "",
    render: (d) => <span className={`px-2 py-0.5 rounded-md text-[11px] whitespace-nowrap ${STAGE_COLORS[d.stage] || "bg-[#2c2c2c] text-[#b4b4b4]"}`}>{d.stage || "—"}</span> },
  { k: "broker", label: "Brokerage / Broker", align: "left", w: "min-w-[11rem]", get: (d) => `${d.brokerFirm || ""} ${d.brokerName || ""}`,
    render: (d) => (
      <div className="min-w-0">
        <div className="text-[#b4b4b4] truncate">{d.brokerFirm || "—"}</div>
        <div className="text-[11px] text-[#8a8a8a] truncate">{d.brokerName || ""}</div>
      </div>
    ) },
  { k: "sf", label: "SF", align: "right", get: (d) => d.squareFeet || 0, render: (d) => sf0(d.squareFeet) },
  { k: "offer", label: "Offer", align: "right", get: (d) => d.offerPrice || 0,
    render: (d) => <span className="text-[#f04f44]">{fmtMoneyShort(d.offerPrice)}</span> },
  { k: "psf", label: "$/SF", align: "right", get: (d) => (d.offerPrice && d.squareFeet ? d.offerPrice / d.squareFeet : 0),
    render: (d) => (d.offerPrice && d.squareFeet ? `$${Math.round(d.offerPrice / d.squareFeet)}` : "—") },
  { k: "loi", label: "LOI Date", align: "right", get: (d) => d.loiDate || "", render: (d) => fmtShortDate(d.loiDate) },
  { k: "receipt", label: "Receipt", align: "center", get: (d) => d.confirmedReceiptAt || "",
    render: (d) => (d.confirmedReceiptAt ? <span className="text-[#7cbb99]" title={fmtShortDate(d.confirmedReceiptAt)}>✓</span> : <span className="text-[#6b6b6b]">—</span>) },
  { k: "counter", label: "Counter", align: "right", get: (d) => d.counterPrice || (d.counteredAt ? 1 : 0),
    render: (d) => (d.counteredAt ? <span className="text-[#f4f4f4]">{d.counterPrice ? fmtMoneyShort(d.counterPrice) : "✓"}</span> : <span className="text-[#6b6b6b]">—</span>) },
  { k: "follow", label: "Follow-up", align: "right", get: (d) => d.followUpDate || "9999",
    render: (d) => {
      const due = followUpDays(d);
      if (due == null) return <span className="text-[#6b6b6b]">—</span>;
      return <span className={due < 0 ? "text-[#f04f44] font-semibold" : due === 0 ? "text-[#c8922f]" : "text-[#b4b4b4]"}>
        {due < 0 ? `${-due}d overdue` : due === 0 ? "Today" : fmtShortDate(d.followUpDate)}
      </span>;
    } },
];

export default function OutreachTable({ deals, onSelectDeal }) {
  const [sort, setSort] = useState({ k: "loi", dir: "desc" });
  const rows = useMemo(() => {
    const out = deals.map((d) => ({ ...d, _name: named(d) }));
    const col = COLUMNS.find((c) => c.k === sort.k) || COLUMNS[0];
    return out.sort((a, b) => {
      const va = col.get(a), vb = col.get(b);
      const cmp = typeof va === "string" ? String(va).localeCompare(String(vb)) : (va - vb);
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [deals, sort]);
  const toggleSort = (k) => setSort((s) => (s.k === k ? { k, dir: s.dir === "asc" ? "desc" : "asc" } : { k, dir: "desc" }));

  return (
    <div className="bg-[#242424] border border-[#3a3a3a] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#3a3a3a]">
              {COLUMNS.map((c) => (
                <th key={c.k} onClick={() => toggleSort(c.k)}
                  className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider cursor-pointer select-none
                    ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"}
                    ${sort.k === c.k ? "text-[#f04f44]" : "text-[#8a8a8a] hover:text-[#b4b4b4]"} ${c.w || ""}`}>
                  {c.label}{sort.k === c.k && <span className="ml-1">{sort.dir === "asc" ? "↑" : "↓"}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#303030]">
            {rows.map((d) => (
              <tr key={d.id} className="hover:bg-[#2c2c2c] cursor-pointer" onClick={() => onSelectDeal?.(d)}>
                {COLUMNS.map((c) => (
                  <td key={c.k} className={`px-3 py-2 text-[12.5px] tabular-nums text-[#b4b4b4]
                    ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"}`}>
                    {c.render(d)}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-[12px] text-[#8a8a8a]">No LOIs match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
